import { useCallback } from 'react';
import * as XLSX from 'xlsx';

type ExportOptions<T> = {
  headers?: Partial<Record<keyof T, string>>; // ✅ تخصيص عناوين الأعمدة
  transformData?: (item: T) => Partial<T>; // ✅ تعديل البيانات قبل التصدير
  filename?: string; // ✅ اسم الملف
};

function useExportExcelCustom<T>(
  data: T[],
  options: ExportOptions<T> = { filename: 'export.xlsx' },
) {
  return useCallback(() => {
    if (!data || data.length === 0) {
      console.error('No data available to export');
      return;
    }

    let processedData: Partial<T>[] = options.transformData
      ? data.map((item) => options.transformData!(item))
      : [...data];

    let worksheet;
    if (options.headers) {
      const headerKeys = Object.keys(options.headers); // مفاتيح الحقول المطلوبة
      const headerValues = Object.values(options.headers); // أسماء الأعمدة المخصصة

      // ✅ التأكد من أن البيانات تحتوي فقط على الحقول المحددة في `headers`
      processedData = processedData.map((item) =>
        headerKeys.reduce((acc, key) => {
          if (key in item) {
            acc[key as keyof T] = item[key as keyof T];
          }
          return acc;
        }, {} as Partial<T>),
      );

      // ✅ إنشاء ورقة العمل باستخدام `json_to_sheet` وإضافة `headerKeys` لتحديد الأعمدة
      worksheet = XLSX.utils.json_to_sheet(processedData, {
        header: headerKeys,
      });

      // ✅ استبدال الصف الأول بمسميات الأعمدة المخصصة
      XLSX.utils.sheet_add_aoa(worksheet, [headerValues], { origin: 'A1' });
    } else {
      // ✅ إذا لم تكن هناك عناوين، استخدم البيانات مباشرةً
      worksheet = XLSX.utils.json_to_sheet(processedData);
    }

    // ✅ إنشاء ملف Excel جديد وإضافة الشيت إليه
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, options.filename || 'export.xlsx');
  }, [data, options]);
}

export default useExportExcelCustom;
